import React, { useState, useMemo } from 'react';
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Chip,
  Grid
} from '@mui/material';
import CustomTable from 'src/ui-component/CustomTable/Index';
import CustomSelect from 'src/ui-component/CustomSelect';
import CustomSearchBar from 'src/ui-component/CustomSearchBar';

export interface Service {
  ServiceName: string;
  Status: string;
  SubscriptionStartDate: string;
  SubscriptionEndDate: string;
  SubscriptionFee: number;
}

interface Props {
  services: Service[];
}

const ServiceSubscriptions: React.FC<Props> = ({ services }) => {
  const [editService, setEditService] = useState<Service | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [pagination, setPagination] = useState(1);
  const [isSearch, setIsSearch] = useState('');
  const [searchVal, setSearchVal] = useState('');
  const [customSelectValue, setCustomSelectValue] = useState('All');
  const [filteredRows, setFilteredRows] = useState<any[]>([]);

  const columns = [
    'Service Name',
    'Status',
    'Subscription Start Date',
    'Subscription End Date',
    'Subscription Fee'
  ];

  // Transform services data for the table
  const rows = useMemo(() => {
    return services.map((service, index) => [
      service.ServiceName,
      <Chip
        key={`status-chip-${index}`}
        label={service.Status}
        color={service.Status === 'Active' ? 'success' : 'error'}
        variant="outlined"
      />,
      service.SubscriptionStartDate
        ? new Date(service.SubscriptionStartDate).toLocaleDateString('en-US')
        : '',
      service.SubscriptionEndDate
        ? new Date(service.SubscriptionEndDate).toLocaleDateString('en-US')
        : '',
      service.SubscriptionFee ? service.SubscriptionFee.toFixed(2) : '0'
    ]);
  }, [services]);

  // Handle status change confirmation
  const handleConfirmStatusChange = () => {
    if (editService) {
      editService.Status =
        editService.Status === 'Active' ? 'In Active' : 'Active';
    }
    setOpenDialog(false);
    setEditService(null);
  };

  // Handle status change cancel
  const handleCancel = () => {
    setOpenDialog(false);
    setEditService(null);
  };

  if (services.length === 0) {
    return <Typography>No Service Subscriptions Available</Typography>;
  }

  return (
    <>
      <Typography variant="h6" gutterBottom p={1}>
        Service Subscriptions Details
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item md={3} lg={3} xl={2} xs={12} sm={12}>
          <CustomSelect
            setpagination={setPagination}
            filterValue={customSelectValue}
            setFilterValue={setCustomSelectValue}
            title="Filter by Status"
            options={[
              { key: 'All', value: 'All' },
              { key: 'Active', value: 'Active' },
              { key: 'InActive', value: 'InActive' }
            ]}
          />
        </Grid>

        <Grid item md={3} lg={3} xl={2} xs={12} sm={12}>
          <CustomSearchBar
            rows={rows}
            setFilteredRows={setFilteredRows}
            setpagination={setPagination}
            setIsSearch={setIsSearch}
            searchVal={searchVal}
            setSearchVal={setSearchVal}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <CustomTable
          pagination={pagination}
          setSelectedRowData={() => {}}
          rows={filteredRows.length > 0 ? filteredRows : rows}
          columns={columns}
          isSearch={isSearch}
        />
      </Box>

      <Dialog open={openDialog} onClose={handleCancel}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to change the status of the service{' '}
            <strong>{editService?.ServiceName}</strong> to{' '}
            <strong>
              {editService?.Status === 'Active' ? 'In Active' : 'Active'}
            </strong>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmStatusChange} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ServiceSubscriptions;
